import { Button, Grid, Typography } from '@mui/material';
import * as React from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import StringResourceManager, { GetString } from '../StringResourceManager';
import {
  Checkbox,
  Input,
  MaskedTextBox,
  TextBox,
} from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import clsx from 'clsx';
import { TEXT_MESSAGE_FIELD_MASK } from '../../utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useEffect } from 'react';
import {
  fetchRegisteredUsers,
  registerUser,
  updateUser,
} from '../../store/Notifications/NotificationsActions';
import { Dispatch } from 'redux';
import { fetchJsonData, isNullOrEmptyString } from '../../utils/utils';
import { NotificationUser } from '../../Interfaces/Notifications/NotificationUser';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridAlign: {
      display: 'flex',
      alignItems: 'center',
      marginTop: '10px',
    },
    alignGridItems: {
      display: 'flex',
      flexDirection: 'column',
    },
    box: {
      height: 60,
      display: 'flex',
      borderRadius: 5,
      padding: theme.spacing(2),
      margin: theme.spacing(1),
      backgroundColor: '#fff',
    },
    spreadBox: {
      justifyContent: 'space-around',
      alignItems: 'center',
    },
    dialogAction: {
      height: '3rem',
      width: '7em',
      padding: theme.spacing(2),
    },
  })
);

interface Props {
  user: string;
  readOnlyAccess: boolean;
  closeUserDetailsForm: () => void;
  showRegistrationStatus: () => void;
  editView: boolean;
}

const UserDetailsForm: React.FC<Props> = ({
  user,
  readOnlyAccess,
  closeUserDetailsForm,
  showRegistrationStatus,
  editView,
}): React.ReactElement => {
  const classes = useStyles();
  const [userId, setUserId] = React.useState<number>(0);
  const [emailChecked, setEmailChecked] = React.useState(false);
  const [contactChecked, setContactChecked] = React.useState(false);
  const [username, setUsername] = React.useState<string>('');
  const [initialUsername, setInitialUsername] = React.useState<string>('');
  const [userEmail, setUserEmail] = React.useState<string>('');
  const [userPhoneNumber, setUserPhoneNumber] = React.useState<string>('');
  const [isValidUsername, setIsValidUsername] = React.useState(false);
  const [isValidUserEmail, setIsValidUserEmail] = React.useState(false);
  const [isValidUserPhoneNumber, setIsValidUserPhoneNumber] =
    React.useState(false);

  const SELECT_ALL = GetString('SELECT_ALL');

  const dispatch: Dispatch<any> = useDispatch();
  const token: string | undefined = useSelector(
    (state: ApplicationState) => state.userSessionReducer.sessionInfo?.Token
  );
  const fetchUserByUserName = async (
    userName: string,
    token: string | undefined
  ) => {
    const data = await fetchJsonData(`users/userdata/${userName}`, token);
    console.log(`Dispatching RECEIVED_USER_DATA ${userName}`);
    return data as NotificationUser;
  };

  useEffect((): ReturnType<React.EffectCallback> => {
    if (user === '') {
      setUserId(0);
      setUsername('');
      setUserEmail('');
      setUserPhoneNumber('');
      setEmailChecked(false);
      setContactChecked(false);
      setIsValidUserEmail(false);
      setIsValidUsername(false);
      setIsValidUserPhoneNumber(false);
      setInitialUsername('');
    } else {
      fetchUserByUserName(user, token)
        .then(data => {
          setUserId(data.UserId);
          setUsername(data.UserName);
          setInitialUsername(data.UserName);
          setUserEmail(data.EmailAddress);
          setUserPhoneNumber(data.PhoneNumber);
          editView &&
            (setIsValidUserEmail(true),
            setIsValidUsername(true),
            setIsValidUserPhoneNumber(true));
          if (data.EmailAddress) {
            setEmailChecked(true);
          } else {
            setEmailChecked(false);
            setIsValidUserEmail(false);
          }
          if (data.PhoneNumber) {
            setContactChecked(true);
          } else {
            setContactChecked(false);
            setIsValidUserPhoneNumber(false);
          }
        })
        .catch(error => {
          console.error(error);
        });
    }
    return (): void => {};
  }, [user, editView]);

  const isUserNameInvalid = (name: string) => {
    if (isNullOrEmptyString(name)) return true;
    if (name === SELECT_ALL) return true;
    if (initialUsername === name) return false;
    return registeredUsers.some(user => user.UserName === name);
  };
  const isEmailValid = (email: string) => {
    const emailRegex = new RegExp(/\S+@\S+\.\S+/);
    return emailRegex.test(email);
  };
  const isPhoneNumberValid = (contact: string) => {
    const contactRegex = new RegExp(/^[0-9]{10}$/);
    return contactRegex.test(contact);
  };
  const handleUsernameChange = (event: any) => {
    const name = event.target.value;
    setUsername(name);
    setIsValidUsername(!isUserNameInvalid(name));
  };
  const handleUserEmailChange = (event: any) => {
    const email = event.target.value;
    setUserEmail(email);
    setIsValidUserEmail(isEmailValid(email));
  };
  const handleUserPhoneNumberChange = (event: any) => {
    const phoneNumber = event.target.value;
    setUserPhoneNumber(phoneNumber);
    setIsValidUserPhoneNumber(isPhoneNumberValid(phoneNumber));
  };
  const handleEmailChecked = (event: any) => {
    setEmailChecked(event.value);
  };
  const handleTextChecked = (event: any) => {
    setContactChecked(event.value);
  };
  const registeredUsers: NotificationUser[] = useSelector(
    (state: ApplicationState) => state.notificationsReducer.registeredUsers
  );
  useEffect((): ReturnType<React.EffectCallback> => {
    fetchRegisteredUsers(dispatch);
    return (): void => {};
  }, []);

  const handleSave = () => {
    let payload: NotificationUser = {
      UserId: 0,
      UserName: username,
      EmailAddress: emailChecked ? userEmail : '',
      PhoneNumber: contactChecked ? userPhoneNumber : '',
      Active: true,
    };
    if (editView) {
      payload.UserId = userId;
      updateUser(dispatch, payload).then(() => {
        fetchRegisteredUsers(dispatch);
      });
    } else {
      registerUser(dispatch, payload).then(() => {
        fetchRegisteredUsers(dispatch);
      });
    }
    showRegistrationStatus();
    handleClose();
  };

  const handleClose = () => {
    closeUserDetailsForm();
  };

  const isSaveDisabled = () => {
    return (
      !isValidUsername ||
      (emailChecked && !isValidUserEmail) ||
      (contactChecked && !isValidUserPhoneNumber) ||
      (!emailChecked && !contactChecked)
    );
  };

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        className={classes.gridAlign}
        style={{ width: '350px' }}
      >
        <Grid item xs={12}>
          <StringResourceManager
            variant="body1"
            style="dialogLabel"
            stringKey={'USER_DETAILS'}
            gutterBottom={true}
          />
        </Grid>

        <Grid
          item
          xs={4}
          style={{ visibility: userId === 0 ? 'hidden' : 'visible' }}
        >
          <Typography variant="body1">{GetString('USER_ID')}</Typography>
        </Grid>
        <Grid
          item
          xs={8}
          style={{ visibility: userId === 0 ? 'hidden' : 'visible' }}
        >
          <TextBox readOnly={true} value={userId}></TextBox>
        </Grid>

        <Grid item xs={4} style={{ marginBottom: '20px' }}>
          <Typography variant="body1">{GetString('USER_NAME')}</Typography>
        </Grid>
        <Grid item xs={8}>
          <div className={classes.alignGridItems}>
            <Input
              readOnly={readOnlyAccess}
              value={username}
              onChange={handleUsernameChange}
              valid={isValidUsername}
              required={true}
              ariaDescribedBy={GetString('RULE_NAME_ERROR_MESSAGE')}
              validityStyles={!readOnlyAccess}
            ></Input>
            <Error
              id={GetString('RULE_NAME_ERROR_MESSAGE')}
              style={{
                visibility:
                  isValidUsername || readOnlyAccess ? 'hidden' : 'visible',
              }}
            >
              {username === SELECT_ALL
                ? GetString('INVALID_USER_NAME_ERROR')
                : GetString('RULE_NAME_ERROR_MESSAGE')}
            </Error>
          </div>
        </Grid>
        <Grid item xs={4}>
          <Checkbox
            disabled={readOnlyAccess}
            checked={emailChecked}
            onChange={handleEmailChecked}
            label={GetString('EMAIL_STR')}
          ></Checkbox>
        </Grid>
        <Grid
          item
          xs={8}
          style={{ visibility: emailChecked ? 'visible' : 'hidden' }}
        >
          <Input
            readOnly={readOnlyAccess}
            type="email"
            value={userEmail}
            onChange={handleUserEmailChange}
            valid={isValidUserEmail}
            validityStyles={!readOnlyAccess}
          ></Input>
        </Grid>
        <Grid item xs={4}>
          <Checkbox
            disabled={readOnlyAccess}
            checked={contactChecked}
            onChange={handleTextChecked}
            label={GetString('PHONE_NUMBER_STR')}
          ></Checkbox>
        </Grid>
        <Grid
          item
          xs={8}
          style={{ visibility: contactChecked ? 'visible' : 'hidden' }}
        >
          <MaskedTextBox
            readonly={readOnlyAccess}
            mask={TEXT_MESSAGE_FIELD_MASK}
            prompt={''}
            value={userPhoneNumber}
            onChange={handleUserPhoneNumberChange}
            valid={isValidUserPhoneNumber}
            validityStyles={!readOnlyAccess}
          ></MaskedTextBox>
        </Grid>
        <Grid item xs className={clsx(classes.spreadBox, classes.box)}>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={readOnlyAccess || isSaveDisabled()}
            className={classes.dialogAction}
            onClick={handleSave}
          >
            {GetString('SAVE_BUTTON')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={classes.dialogAction}
            onClick={handleClose}
          >
            {GetString('CLOSE_STR')}
          </Button>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default UserDetailsForm;
